import { type BenchmarkEntry } from "@/types/SharedTypes";
import { useCallback, useEffect, useState, type PropsWithChildren } from "react";
import { BenchmarkContext, type ScanResultType } from "./BenchmarkContext";

export const BenchmarkProvider = ({ children }: PropsWithChildren) => {
    const [scanResults, setScanResults] = useState<ScanResultType>({});
    const [startTime, setStartTime] = useState<number | null>(null);
    const [endTime, setEndTime] = useState<number | null>(null);
    const [entries, setEntries] = useState<BenchmarkEntry[]>([]);

    const startBenchmark = useCallback(() => setStartTime((prev) => prev === null ? performance.now() : prev),[]);
    const stopBenchmark = useCallback(() => setEndTime((prev) => prev === null ? performance.now() : prev),[]);
    
    const addBenchmarkEntry = useCallback((entry: BenchmarkEntry) => {
        if (!startTime) {
            startBenchmark();
        }
        setEntries((prevEntries) => [...prevEntries, entry]);
    },[startTime, startBenchmark]);

    const getElapsedTimeMs = useCallback(() => {
        if (startTime === null) return 0;
        if (endTime === null) return performance.now() - startTime;
        return endTime - startTime;
    },[startTime, endTime]);

    const captureScanResult = useCallback((result: ScanResultType) => {
        if (result.qrOne) addBenchmarkEntry({ name: "Scan - QROne Captured", durationMs: getElapsedTimeMs(), timestamp: Date.now() });
        else if (result.qrTwo) addBenchmarkEntry({ name: "Scan - QRTwo Captured", durationMs: getElapsedTimeMs(), timestamp: Date.now() });
        else if (result.serial) addBenchmarkEntry({ name: "Scan - Serial Number Captured", durationMs: getElapsedTimeMs(), timestamp: Date.now() });
        setScanResults((prev) => ({
            qrOne: prev.qrOne || result.qrOne,
            qrTwo: prev.qrTwo || result.qrTwo,
            serial: prev.serial || result.serial,
        }));
    }, [addBenchmarkEntry, getElapsedTimeMs]);

    useEffect(() => {
        const originalConsoleLog = console.log;
        const originalConsoleError = console.error;
        const originalConsoleWarn = console.warn;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        console.log = (...args: any[]) => {
            addBenchmarkEntry({ 
                name: `(Log) - ${args.length > 0 ? (typeof args[0] === 'string' ? args[0] : JSON.stringify(args[0])) : ''}`, 
                durationMs: 0, 
                timestamp: Date.now()
            });
            originalConsoleLog(...args);
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        console.error = (...args: any[]) => {
            addBenchmarkEntry({ 
                name: `(Error) - ${args.length > 0 ? (typeof args[0] === 'string' ? args[0] : JSON.stringify(args[0])) : ''}`, 
                durationMs: 0, 
                timestamp: Date.now()
            });
            originalConsoleError(...args);
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        console.warn = (...args: any[]) => {
            addBenchmarkEntry({ 
                name: `(Warn) - ${args.length > 0 ? (typeof args[0] === 'string' ? args[0] : JSON.stringify(args[0])) : ''}`, 
                durationMs: 0, 
                timestamp: Date.now()
            });
            originalConsoleError(...args);
        }
        return () => {
            console.log = originalConsoleLog;
            console.error = originalConsoleError;
            console.warn = originalConsoleWarn;
        };
    }, [addBenchmarkEntry]);

    return (
        <BenchmarkContext.Provider value={{ 
            scanResults, 
            entries, 
            startTime, 
            endTime, 
            addBenchmarkEntry, 
            startBenchmark, 
            stopBenchmark, 
            getElapsedTimeMs, 
            captureScanResult 
        }}>
            {children}
        </BenchmarkContext.Provider>
    );
}